import ExcelJS from 'exceljs';

export const MAX_EXCEL_FILE_SIZE = 10 * 1024 * 1024;

export const MAX_EXCEL_FILE_SIZE_MB = Math.round(MAX_EXCEL_FILE_SIZE / (1024 * 1024));

export const ACCEPTED_EXTENSIONS = ['xlsx', 'csv'];

const ACCEPTED_MIME_TYPES = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    'text/plain',
    'application/csv',
    'application/vnd.ms-excel',
    'application/octet-stream',
    '',
];

export function getExtension(fileName) {
    return String(fileName ?? '').split('.').pop().toLowerCase();
}

export function isMimeAcceptable(mimeType) {
    if (mimeType === null || mimeType === undefined || mimeType === '') {
        return true;
    }

    return ACCEPTED_MIME_TYPES.includes(mimeType);
}

export function formatExcelDate(date) {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

function cleanLiteral(text) {
    return text
        .replace(/\[\$([^\]-]*)[^\]]*\]/g, '$1')
        .replace(/\[[^\]]*\]/g, '')
        .replace(/"([^"]*)"/g, '$1')
        .replace(/\\(.)/g, '$1')
        .replace(/[_*](.)/g, '');
}

export function formatExcelNumber(value, numFmt) {
    if (!numFmt || numFmt === 'General' || numFmt === '@') {
        return String(value);
    }

    const section = numFmt.split(';')[0];

    if (/[eE][+-]?0/.test(section)) {
        return String(value);
    }

    const match = section.match(/[0#][0#,]*(\.[0#]+)?/);

    if (!match) {
        return String(value);
    }

    const pattern = match[0];
    const isPercent = section.includes('%');
    const prefix = cleanLiteral(section.slice(0, match.index));
    const suffix = cleanLiteral(section.slice(match.index + pattern.length));

    const number = isPercent ? value * 100 : value;

    const hasDecimal = pattern.includes('.');
    const decimals = hasDecimal ? (pattern.split('.')[1] || '').length : 0;
    const useGrouping = pattern.split('.')[0].includes(',');
    const integerPattern = pattern.split('.')[0].replace(/,/g, '');

    let out;

    if (!hasDecimal && /^0+$/.test(integerPattern) && integerPattern.length > 1) {
        const sign = number < 0 ? '-' : '';
        out = sign + String(Math.round(Math.abs(number))).padStart(integerPattern.length, '0');
    } else {
        out = number.toLocaleString('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
            useGrouping,
        });
    }

    return prefix + out + suffix;
}

export function normalizeExcelValue(cell) {
    if (!cell) {
        return '';
    }

    let value = cell.value;
    const numFmt = cell.numFmt;

    if (value === null || value === undefined) {
        return '';
    }

    if (typeof value === 'object' && !(value instanceof Date)) {
        if (Array.isArray(value.richText)) {
            return value.richText.map(part => part.text ?? '').join('');
        }

        if (Object.prototype.hasOwnProperty.call(value, 'result')) {
            value = value.result;
        } else if (Object.prototype.hasOwnProperty.call(value, 'text')) {
            return String(value.text ?? '');
        } else if (Object.prototype.hasOwnProperty.call(value, 'error')) {
            return String(value.error ?? '');
        } else {
            return '';
        }
    }

    if (value === null || value === undefined) {
        return '';
    }

    if (value instanceof Date) {
        return formatExcelDate(value);
    }

    if (typeof value === 'boolean') {
        return value ? 'TRUE' : 'FALSE';
    }

    if (typeof value === 'number') {
        return formatExcelNumber(value, numFmt);
    }

    return String(value);
}

export function worksheetToMatrix(worksheet) {
    const matrix = [];
    const columnCount = worksheet.columnCount;
    const rowCount = worksheet.rowCount;

    for (let r = 1; r <= rowCount; r++) {
        const row = worksheet.getRow(r);
        const cells = [];

        for (let c = 1; c <= columnCount; c++) {
            cells.push(normalizeExcelValue(row.getCell(c)));
        }

        matrix.push(cells);
    }

    return matrix;
}

export function matrixToJson(matrix) {
    if (!matrix.length) {
        return [];
    }

    const headerCells = matrix[0];
    const columns = [];
    const usedKeys = {};
    let emptyIndex = 0;

    headerCells.forEach((raw, idx) => {
        const value = raw === null || raw === undefined ? '' : String(raw);
        let key;

        if (value === '') {
            key = emptyIndex === 0 ? '__EMPTY' : `__EMPTY_${emptyIndex}`;
            emptyIndex++;
        } else if (usedKeys[value] !== undefined) {
            usedKeys[value]++;
            key = `${value}_${usedKeys[value]}`;
        } else {
            usedKeys[value] = 0;
            key = value;
        }

        columns.push({ idx, key });
    });

    const rows = [];

    for (let r = 1; r < matrix.length; r++) {
        const source = matrix[r];
        const obj = {};
        let hasValue = false;

        columns.forEach(({ idx, key }) => {
            const cell = source[idx];
            const value = cell === null || cell === undefined ? '' : cell;

            if (value !== '') {
                hasValue = true;
            }

            obj[key] = value;
        });

        if (hasValue) {
            rows.push(obj);
        }
    }

    return rows;
}

export function parseCsv(text) {
    if (text.charCodeAt(0) === 0xFEFF) {
        text = text.slice(1);
    }

    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;

    for (let i = 0; i < text.length; i++) {
        const ch = text[i];

        if (inQuotes) {
            if (ch === '"') {
                if (text[i + 1] === '"') {
                    field += '"';
                    i++;
                } else {
                    inQuotes = false;
                }
            } else {
                field += ch;
            }
        } else if (ch === '"') {
            inQuotes = true;
        } else if (ch === ',') {
            row.push(field);
            field = '';
        } else if (ch === '\r') {
            continue;
        } else if (ch === '\n') {
            row.push(field);
            rows.push(row);
            row = [];
            field = '';
        } else {
            field += ch;
        }
    }

    if (field !== '' || row.length) {
        row.push(field);
        rows.push(row);
    }

    return rows;
}

export async function parseXlsxBuffer(buffer) {
    const workbook = new ExcelJS.Workbook();

    await workbook.xlsx.load(buffer);

    const worksheet = workbook.worksheets[0];

    if (!worksheet) {
        return [];
    }

    return matrixToJson(worksheetToMatrix(worksheet));
}

export function parseCsvBuffer(buffer) {
    const text = new TextDecoder('utf-8').decode(buffer);

    return matrixToJson(parseCsv(text));
}

export async function parseSpreadsheetBuffer(buffer, extension) {
    if (extension === 'csv') {
        return parseCsvBuffer(buffer);
    }

    return parseXlsxBuffer(buffer);
}
